import { API_BASE_URL, parseIstatecResponse } from './apiConfig';

/**
 * A single entry in the "Research in practice" catalog. Mirrors the
 * document shape stored in Mongo's
 * `in_parctice_research_and_documentsion` collection, with `_id`
 * pre-serialised to a plain string by the backend so the FE never
 * has to touch ObjectId types.
 *
 * Every field is treated as OPTIONAL on the FE — the backend
 * pass-through is permissive by design, and an early / partially
 * seeded row shouldn't crash the whole slider on render.
 */
export interface ResearchEntry {
  /** Stringified Mongo _id — used as the React list key. */
  id: string;
  /** Ascending sort order. Also doubles as the URL param on the detail page. */
  sorting_id?: number;
  /** Which "What We Do" services this entry demonstrates. */
  service_categories?: string[];
  /** Facet tags — currently "Research" and/or "Implementation". */
  categories?: string[];
  header?: string;
  description?: string;
  cover_image_url?: string;
  research_documentation_url?: string;
  website_url?: string;
  /**
   * Industries this piece of work spans. Modelled as an ARRAY because
   * a single pipeline / product frequently applies to multiple
   * verticals — e.g. Doceract is used in Banking + Insurance +
   * Healthcare. The old `industry` (singular string) shape is still
   * accepted below for backwards compatibility with any older
   * document that hasn't been migrated yet.
   */
  industries?: string[];
  /** @deprecated Use `industries` (array). Kept so old rows still render. */
  industry?: string;
  intro_video_url?: string;
}

/**
 * Return the entry's industry tags as a normalised string array,
 * whether the underlying document uses the new plural `industries`
 * array or the legacy singular `industry` string. Empty / missing
 * values collapse to an empty array so callers can just `.map(...)`
 * over the result without needing their own guards.
 */
export function getIndustries(entry: ResearchEntry): string[] {
  if (Array.isArray(entry.industries)) {
    return entry.industries.filter(
      (i): i is string => typeof i === 'string' && i.trim().length > 0,
    );
  }
  const single = entry.industry?.trim();
  return single ? [single] : [];
}

/**
 * Two-way classifier for the media URLs on a research entry —
 * `cover_image_url` and `intro_video_url`.
 *
 * The field names are historical: content editors sometimes put a
 * `.webm` file into `cover_image_url` (because the "hero" of an
 * entry is genuinely a video), or a static PNG / GIF into
 * `intro_video_url` (when the entry has no dedicated intro clip
 * yet). The UI should render each slot correctly regardless of the
 * concrete file type on the other side of the URL.
 *
 * Detection is extension-based (rather than a HEAD request) to
 * avoid a second network round-trip per card / page. GIFs are
 * classified as `'image'` because browsers animate them
 * automatically via `<img>` — no `<video>` needed. Anything that
 * looks like a real video container (`.webm`, `.mp4`, `.mov`,
 * `.m4v`, `.ogg`, `.ogv`, `.avi`, `.mkv`) resolves to `'video'`.
 * Everything else — including missing / unrecognised URLs —
 * defaults to `'image'` because the `<img>` element degrades
 * more gracefully (broken-image icon) than a `<video>` element
 * fed a non-video URL (empty player + console noise).
 */
export type MediaKind = 'video' | 'image';

export function detectMediaKind(
  url: string | undefined | null,
): MediaKind {
  if (!url) return 'image';
  // Strip query string / fragment before extension-matching — plenty
  // of CDNs sign URLs with `?v=…` and `?token=…` suffixes.
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  if (/\.(webm|mp4|mov|m4v|ogg|ogv|avi|mkv)$/.test(clean)) return 'video';
  return 'image';
}

export interface ResearchListData {
  count: number;
  items: ResearchEntry[];
}

/**
 * GET /in_practice_research — returns every entry from the catalog,
 * sorted by `sorting_id`. Read-only, no auth, no OTP gate. Fails
 * open: on any network / server error the FE just gets an empty
 * list and the surrounding UI hides the section quietly.
 */
export async function fetchResearchEntries(): Promise<ResearchEntry[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/in_practice_research`);
    // Parse loose — the shared `IstatecResponse<T>` helper types
    // `data` as `Record<string, unknown> | null`, so we cast the
    // `items` payload to our typed shape after a quick shape check.
    const body = await parseIstatecResponse(res);
    if (body.status !== 'success' || !body.data) return [];
    const items = (body.data as unknown as ResearchListData).items;
    return Array.isArray(items) ? items : [];
  } catch {
    // Network / DNS / offline — silently return empty so callers
    // render "no items" instead of an error banner.
    return [];
  }
}
